import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Helmet } from 'react-helmet';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const SquareRootCalculator = () => {
  const [inputData, setInputData] = useState({
    number: '',
    result: '',
    approximateResult: '',
    iterations: 0
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const calculateSquareRoot = () => {
    const { number } = inputData;
    
    // Validate input
    if (!number) {
      setError('Please enter a number');
      return;
    }

    // Validate numeric input
    if (isNaN(number)) {
      setError('Please enter a valid number');
      return;
    }

    // Convert to number
    const numericValue = parseFloat(number);

    // Check for negative numbers
    if (numericValue < 0) {
      setError('Cannot calculate square root of a negative number');
      return;
    }

    // Calculate square root using Math.sqrt
    const preciseResult = Math.sqrt(numericValue);

    // Calculate approximate result using Newton-Raphson method
    let approximateResult = numericValue / 2;
    let previousApproximation = 0;
    let iterations = 0;

    while (Math.abs(approximateResult - previousApproximation) > 0.0001 && iterations < 1000) {
      previousApproximation = approximateResult;
      approximateResult = (approximateResult + numericValue / approximateResult) / 2;
      iterations++;
    }

    // Create visualization data
    const values = [numericValue, preciseResult];
    const labels = ['Input Number', 'Square Root'];

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Values',
        data: values,
        backgroundColor: [
          'rgba(255, 99, 132, 0.5)',
          'rgba(54, 162, 235, 0.5)'
        ],
        borderColor: [
          'rgba(255, 99, 132, 1)',
          'rgba(54, 162, 235, 1)'
        ],
        borderWidth: 1
      }]
    };

    const calculationSteps = [
      'Step 1: Validate input number',
      `Input: ${numericValue}`,
      '\nStep 2: Calculate precise square root using Math.sqrt()',
      `Precise Result: ${preciseResult.toFixed(6)}`,
      '\nStep 3: Calculate approximate square root using Newton-Raphson method',
      `Approximate Result: ${approximateResult.toFixed(6)}`,
      `Iterations: ${iterations}`
    ];

    setResults({
      number: numericValue,
      preciseResult: preciseResult.toFixed(6),
      approximateResult: approximateResult.toFixed(6),
      iterations,
      calculationSteps,
      chartData
    });
  };

  const exampleData = {
    number: '25',
    preciseResult: '5.000000',
    approximateResult: '5.000000',
    iterations: 4
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Square Root Calculator | Math Tool</title>
          <meta name="description" content="Calculate the square root of any non-negative number. This calculator provides both precise and approximate results using the Newton-Raphson method." />
          <meta name="keywords" content="square root calculator, math calculator, root calculator, Newton-Raphson method" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">√</div>
            <h1 className="text-3xl font-bold text-gray-800">Square Root Calculator</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Calculate the square root of any non-negative number. This calculator provides both precise and approximate results using the Newton-Raphson method.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Number:</label>
              <input
                type="text"
                name="number"
                value={inputData.number}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter a non-negative number (e.g., 25)"
              />
            </div>
          </div>

          <button
            onClick={calculateSquareRoot}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Calculate Square Root
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Square Root Calculation</h3>
                  <p className="text-lg">Input Number: {results.number}</p>
                  <p className="text-lg font-bold mt-4">Precise Square Root: {results.preciseResult}</p>
                  <p className="text-lg">Approximate Square Root: {results.approximateResult}</p>
                  <p className="text-lg">Iterations: {results.iterations}</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Details</h3>
                  <p className="text-lg">Method 1: Math.sqrt() function</p>
                  <p className="text-lg">Method 2: Newton-Raphson iterative method</p>
                  <p className="text-lg">Precision: 6 decimal places</p>
                </div>
              </div>

              {results.chartData && (
                <div className="bg-white p-6 rounded-lg shadow-md mb-8">
                  <Bar
                    data={results.chartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      scales: {
                        y: {
                          beginAtZero: true,
                          title: {
                            display: true,
                            text: 'Values'
                          }
                        },
                        x: {
                          title: {
                            display: true,
                            text: 'Categories'
                          }
                        }
                      },
                      plugins: {
                        title: {
                          display: true,
                          text: 'Square Root Visualization'
                        }
                      }
                    }}
                  />
                </div>
              )}

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for the square root of 25:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Input Number: {exampleData.number}</li>
                <li>Precise Square Root: {exampleData.preciseResult}</li>
                <li>Approximate Square Root: {exampleData.approximateResult}</li>
                <li>Iterations: {exampleData.iterations}</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Using Math.sqrt(): Direct calculation</li>
                <li>Using Newton-Raphson: Iterative approximation</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Square Root Calculator:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Number:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input a non-negative number</li>
                </ul>
              </li>
              <li><strong>Click Calculate:</strong> The calculator will determine:
                <ul className="list-disc pl-6 mt-2">
                  <li>The precise square root using Math.sqrt()</li>
                  <li>An approximate square root using the Newton-Raphson method</li>
                  <li>The number of iterations for the approximation</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the precise and approximate results</li>
                  <li>Check the number of iterations for the approximation</li>
                  <li>View the visual representation of the square root</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>The Newton-Raphson method converges quickly for most numbers</li>
                <li>Precise results are calculated using JavaScript's built-in functions</li>
                <li>For perfect squares, both methods will give identical results</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Square Roots <span className="text-blue-600">√</span></h2>
          <div className="text-gray-600">
            <p>
              A square root of a number is a value that, when multiplied by itself, gives the original number. For example, the square root of 25 is 5 because 5 × 5 = 25. Here's what you should know:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Perfect Squares:</strong> Numbers that have integer square roots (e.g., 4, 9, 16)</li>
              <li><strong>Irrational Numbers:</strong> Square roots of non-perfect squares are irrational (e.g., √2 ≈ 1.41421356)</li>
              <li><strong>Newton-Raphson Method:</strong> An iterative numerical method for finding successively better approximations to the roots of a real-valued function</li>
              <li><strong>Applications:</strong> Square roots are used in geometry, physics, engineering, and many other fields</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Non-Negative Numbers:</strong> Square roots of negative numbers are complex and not calculated here</li>
              <li><strong>Validation:</strong> Always validate inputs to ensure accurate results</li>
              <li><strong>Precision:</strong> Understand the difference between precise and approximate methods</li>
              <li><strong>Iteration Limits:</strong> The Newton-Raphson method has a maximum iteration limit to prevent infinite loops</li>
            </ul>
            <p className="italic mt-4">
              Note: This calculator provides both precise and approximate results. For most practical purposes, the precise result should be sufficient.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default SquareRootCalculator;